import { useMutation, useQuery } from "@apollo/client";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { toast } from "react-toastify";
import { GET_BANDS } from "../../components/BandPicker/graphql";
import { CREATE_BOOKING, DELETE_BOOKING } from "../booking/graphql";
import type { EditableBand } from ".";
import { UPDATE_BOOKINGS } from "./graphql";
import { GET_ALL_STAGES } from "../stages/graphql";

const DRAG_PAYLOAD_TYPE = "application/x-admin-booking-calendar";
const NO_STAGE_ID = "__NO_STAGE__";
const TEMP_BOOKING_PREFIX = "__NEW_BOOKING__";
const DEFAULT_START_HOUR = 10;
const DEFAULT_END_HOUR = 28;
const MAX_END_HOUR = 30;
const SNAP_MINUTES = 15;
const MIN_DURATION_MINUTES = 15;
const DEFAULT_NEW_BOOKING_DURATION_MINUTES = 45;
const PIXELS_PER_MINUTE = 1.25;
const MIN_CARD_HEIGHT_PX = 26;
const PAN_DISABLED_SELECTOR =
  "button, a, input, textarea, select, [role='button'], [draggable='true'], [data-calendar-no-pan='true']";
const SUPPRESS_AUTO_EXPORT_TOAST_CONTEXT = {
  options: { suppressToast: true },
  skipAutoExportMessage: true,
};

type StageColumn = {
  id: string;
  name: string;
  order?: number | null;
};

type BookingDragPayload = {
  type: "booking";
  bookingId: string;
  offsetMinutes: number;
};

type BandDragPayload = {
  type: "band";
  bandId: string;
  bandName: string;
  offsetMinutes?: number;
};

type AnnouncementBookingDragPayload = {
  type: "announcementBooking";
  bookingId: string;
  announcementId: string;
  announcementName: string;
  bandId: string;
  bandName: string;
  offsetMinutes?: number;
};

type DragPayload =
  | BookingDragPayload
  | BandDragPayload
  | AnnouncementBookingDragPayload;

type ResizeState = {
  bookingId: string;
  initialClientY: number;
  initialEndMinutes: number;
  currentEndMinutes: number;
  startMinutes: number;
};

type CalendarBooking = EditableBand & {
  isNew?: boolean;
  announcementId?: string | null;
};

type AnnouncementOption = {
  id: string;
  name: string;
};

type AnnouncementBandOption = {
  bookingId: string;
  announcementId: string;
  announcementName: string;
  bandId: string;
  bandName: string;
};

type BookingEditState = {
  bookingId: string;
  date: string;
  stageId: string;
};

type BookingPosition = {
  booking: CalendarBooking;
  stageId: string;
  startMinutes: number;
  endMinutes: number;
  laneIndex: number;
  laneCount: number;
  top: number;
  height: number;
};

interface CalendarViewProps {
  dayKey: string;
  bands: EditableBand[];
  announcements: AnnouncementOption[];
  announcementBands: AnnouncementBandOption[];
  onRefresh?: () => Promise<unknown>;
}

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

const snapMinutes = (value: number): number => {
  return Math.round(value / SNAP_MINUTES) * SNAP_MINUTES;
};

const safeTimeRange = (start: number, end: number) => {
  if (end <= start) {
    return { start, end: start + MIN_DURATION_MINUTES };
  }

  return { start, end };
};

const parseDragPayload = (raw: string): DragPayload | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as DragPayload;
    if (parsed.type === "booking") {
      if (typeof parsed.bookingId !== "string") return null;
      if (typeof parsed.offsetMinutes !== "number") return null;
      return parsed;
    }

    if (parsed.type === "band") {
      if (typeof parsed.bandId !== "string") return null;
      if (typeof parsed.bandName !== "string") return null;
      if (
        parsed.offsetMinutes !== undefined &&
        typeof parsed.offsetMinutes !== "number"
      ) {
        return null;
      }

      return parsed;
    }

    if (parsed.type === "announcementBooking") {
      if (typeof parsed.bookingId !== "string") return null;
      if (typeof parsed.announcementId !== "string") return null;
      if (typeof parsed.announcementName !== "string") return null;
      if (typeof parsed.bandId !== "string") return null;
      if (typeof parsed.bandName !== "string") return null;
      if (
        parsed.offsetMinutes !== undefined &&
        typeof parsed.offsetMinutes !== "number"
      ) {
        return null;
      }

      return parsed;
    }

    return null;
  } catch (_error) {
    return null;
  }
};

const getUpdateErrorMessage = (error: unknown): string => {
  if (!error) return "Unknown error";
  if (error instanceof Error) return error.message;
  return "Unknown error";
};

const formatTimelineLabel = (dayStart: Dayjs, minuteOffset: number): string => {
  const timestamp = dayStart.add(minuteOffset, "minute");
  const isNextDay = minuteOffset >= 24 * 60;
  return `${timestamp.format("HH:mm")}${isNextDay ? " (+1)" : ""}`;
};

const shouldStartCalendarPan = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return true;
  return !target.closest(PAN_DISABLED_SELECTOR);
};

const EditEditionBandsCalendarView = ({
  dayKey,
  bands,
  announcements,
  announcementBands,
  onRefresh,
}: CalendarViewProps) => {
  const [bandSearch, setBandSearch] = useState("");
  const [sidebarTab, setSidebarTab] = useState<"announcementBands" | "allBands">(
    "announcementBands"
  );
  const [selectedCreateAnnouncementId, setSelectedCreateAnnouncementId] =
    useState("");
  const [visibleStageIds, setVisibleStageIds] = useState<Set<string>>(
    new Set<string>()
  );
  const [showStageFilters, setShowStageFilters] = useState(false);
  const [isBookingEditorOpen, setIsBookingEditorOpen] = useState(false);
  const [bookingEditState, setBookingEditState] = useState<BookingEditState | null>(
    null
  );
  const [localBands, setLocalBands] = useState<CalendarBooking[]>([]);
  const [activeDropStageId, setActiveDropStageId] = useState<string | null>(
    null
  );
  const [draggingBookingId, setDraggingBookingId] = useState<string | null>(
    null
  );
  const [dirtyBookingIds, setDirtyBookingIds] = useState<Set<string>>(
    new Set<string>()
  );
  const [deletedBookingIds, setDeletedBookingIds] = useState<Set<string>>(
    new Set<string>()
  );
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);
  const resizeStateRef = useRef<ResizeState | null>(null);
  const suppressBookingClickUntilRef = useRef(0);
  const nextTempIdRef = useRef(0);
  const previousDayKeyRef = useRef<string | null>(null);
  const previousStageIdsRef = useRef<Set<string>>(new Set<string>());
  const calendarScrollRef = useRef<HTMLDivElement | null>(null);
  const calendarPanStateRef = useRef<{
    startClientX: number;
    startClientY: number;
    startScrollLeft: number;
    startScrollTop: number;
  } | null>(null);
  const [isCalendarPanning, setIsCalendarPanning] = useState(false);

  const [updateBookings] = useMutation(UPDATE_BOOKINGS);
  const [createBooking] = useMutation(CREATE_BOOKING);
  const [deleteBooking] = useMutation(DELETE_BOOKING);
  const { data: stagesData } = useQuery(GET_ALL_STAGES, {
    fetchPolicy: "cache-first",
  });
  const { data: allBandsData, loading: allBandsLoading } = useQuery(GET_BANDS, {
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (previousDayKeyRef.current === null) {
      previousDayKeyRef.current = dayKey;
      return;
    }

    if (previousDayKeyRef.current === dayKey) {
      return;
    }

    previousDayKeyRef.current = dayKey;
    setBookingEditState(null);
    setIsBookingEditorOpen(false);
    setResizeState(null);
    setDraggingBookingId(null);
    setActiveDropStageId(null);
  }, [dayKey]);

  useEffect(() => {
    setSelectedCreateAnnouncementId((current) => {
      if (current && announcements.some((item) => item.id === current)) {
        return current;
      }

      return announcements[0]?.id ?? "";
    });
  }, [announcements]);

  useEffect(() => {
    setLocalBands((current) => {
      const existingById = new Map<string, CalendarBooking>();
      current.forEach((item) => {
        if (!item.isNew) {
          existingById.set(item.id, item);
        }
      });

      const deduped: CalendarBooking[] = [];
      const seen = new Set<string>();

      bands.forEach((item) => {
        if (seen.has(item.id) || deletedBookingIds.has(item.id)) return;
        seen.add(item.id);

        const previous = existingById.get(item.id);
        const isDirty = dirtyBookingIds.has(item.id);
        const incomingBandName = item.band?.name?.trim() ?? "";
        const band =
          incomingBandName.length > 0 ? item.band : (previous?.band ?? item.band);
        const stage =
          item.stage?.id || !previous?.stage?.id ? item.stage : previous.stage;

        if (isDirty && previous) {
          deduped.push({
            ...item,
            ...previous,
            id: item.id,
            bookingId: item.bookingId,
            band: previous.band ?? band,
            stage: previous.stage ?? stage,
            source: previous.source ?? item.source,
            isNew: false,
          });
          return;
        }

        deduped.push({
          ...(previous ?? {}),
          ...item,
          band,
          stage,
          source: item.source ?? previous?.source,
          isNew: false,
        });
      });

      current.forEach((item) => {
        if (seen.has(item.id) || deletedBookingIds.has(item.id)) return;

        if (item.isNew || dirtyBookingIds.has(item.id)) {
          deduped.push(item);
        }
      });

      return deduped;
    });
  }, [bands, deletedBookingIds, dirtyBookingIds]);

  useEffect(() => {
    resizeStateRef.current = resizeState;
  }, [resizeState]);

  useEffect(() => {
    const resetDragVisualState = () => {
      setDraggingBookingId(null);
      setActiveDropStageId(null);
    };

    window.addEventListener("dragend", resetDragVisualState);
    window.addEventListener("drop", resetDragVisualState);

    return () => {
      window.removeEventListener("dragend", resetDragVisualState);
      window.removeEventListener("drop", resetDragVisualState);
    };
  }, []);

  const selectedDayStart = useMemo(() => {
    const normalizedDayKey = dayKey.replace(/\//g, "-");
    return dayjs.utc(`${normalizedDayKey}T00:00:00Z`);
  }, [dayKey]);

  const getScheduleDayKey = useCallback(
    (value?: string | null, format: "YYYY/MM/DD" | "YYYY-MM-DD" = "YYYY/MM/DD") => {
      if (!value) return null;
      const timestamp = dayjs(value).utc(true);
      if (!timestamp.isValid()) return null;
      const effectiveDay = timestamp.hour() < 5 ? timestamp.subtract(1, "day") : timestamp;
      return effectiveDay.format(format);
    },
    []
  );

  const getMinutesFromDayStart = useCallback(
    (value: string): number => {
      return dayjs.utc(value).diff(selectedDayStart, "minute");
    },
    [selectedDayStart]
  );

  const scheduledBookings = useMemo(() => {
    return localBands.filter((item) => {
      if (!item.startTime || !item.endTime) return false;
      if (!dayjs(item.startTime).isValid() || !dayjs(item.endTime).isValid()) {
        return false;
      }

      return getScheduleDayKey(item.startTime, "YYYY/MM/DD") === dayKey;
    });
  }, [dayKey, getScheduleDayKey, localBands]);

  const unscheduledCount = localBands.length - scheduledBookings.length;

  const stageColumns = useMemo<StageColumn[]>(() => {
    const stageMap = new Map<string, StageColumn>();

    (stagesData?.stages ?? []).forEach((stage) => {
      stageMap.set(stage.id, {
        id: stage.id,
        name: stage.name,
        order: stage.order,
      });
    });

    scheduledBookings.forEach((booking) => {
      if (!booking.stage?.id) return;
      if (!stageMap.has(booking.stage.id)) {
        stageMap.set(booking.stage.id, {
          id: booking.stage.id,
          name: booking.stage.name ?? "Unknown stage",
          order: null,
        });
      }
    });

    const columns = Array.from(stageMap.values()).sort((a, b) => {
      const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return a.name.localeCompare(b.name);
    });

    if (scheduledBookings.some((booking) => !booking.stage?.id)) {
      columns.unshift({
        id: NO_STAGE_ID,
        name: "Unassigned",
      });
    }

    return columns;
  }, [scheduledBookings, stagesData]);

  const stageNameLookup = useMemo(() => {
    const map = new Map<string, string>();
    stageColumns.forEach((stage) => {
      map.set(stage.id, stage.name);
    });
    return map;
  }, [stageColumns]);

  const announcementNameLookup = useMemo(() => {
    const map = new Map<string, string>();
    announcements.forEach((item) => {
      map.set(item.id, item.name);
    });
    return map;
  }, [announcements]);

  const bookingStageOptions = useMemo(() => {
    const stageMap = new Map<string, { name: string; order?: number | null }>();

    (stagesData?.stages ?? []).forEach((stage) => {
      stageMap.set(stage.id, {
        name: stage.name,
        order: stage.order,
      });
    });

    localBands.forEach((item) => {
      if (item.stage?.id && !stageMap.has(item.stage.id)) {
        stageMap.set(item.stage.id, {
          name: item.stage.name ?? "Unknown stage",
          order: null,
        });
      }
    });

    return Array.from(stageMap.entries())
      .map(([id, value]) => ({
        id,
        name: value.name,
        order: value.order,
      }))
      .sort((a, b) => {
        const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        return a.name.localeCompare(b.name);
      });
  }, [localBands, stagesData]);

  const bookingBeingEdited = useMemo(() => {
    if (!bookingEditState) return null;
    return localBands.find((item) => item.id === bookingEditState.bookingId) ?? null;
  }, [bookingEditState, localBands]);

  useEffect(() => {
    const previousStageIds = previousStageIdsRef.current;
    const nextStageIds = new Set(stageColumns.map((stage) => stage.id));

    setVisibleStageIds((current) => {
      const next = new Set<string>();

      stageColumns.forEach((stage) => {
        const isNewStage = !previousStageIds.has(stage.id);
        if (isNewStage || current.has(stage.id)) {
          next.add(stage.id);
        }
      });

      return next;
    });

    previousStageIdsRef.current = nextStageIds;
  }, [stageColumns]);

  const filteredStageColumns = useMemo(
    () => stageColumns.filter((stage) => visibleStageIds.has(stage.id)),
    [stageColumns, visibleStageIds]
  );

  const { minMinute, maxMinute } = useMemo(() => {
    const points: number[] = [];
    scheduledBookings.forEach((booking) => {
      if (!booking.startTime || !booking.endTime) return;
      points.push(getMinutesFromDayStart(booking.startTime));
      points.push(getMinutesFromDayStart(booking.endTime));
    });

    let min = points.length > 0 ? Math.min(...points) : DEFAULT_START_HOUR * 60;
    let max = points.length > 0 ? Math.max(...points) : DEFAULT_END_HOUR * 60;

    min = Math.floor(min / 60) * 60 - 60;
    max = Math.ceil(max / 60) * 60 + 60;

    min = Math.max(0, Math.min(min, DEFAULT_START_HOUR * 60));
    max = Math.max(max, DEFAULT_END_HOUR * 60);
    max = Math.min(MAX_END_HOUR * 60, max);

    if (max - min < 6 * 60) {
      max = Math.min(MAX_END_HOUR * 60, min + 6 * 60);
    }

    return { minMinute: min, maxMinute: max };
  }, [scheduledBookings, getMinutesFromDayStart]);

  const hourMarkers = useMemo(() => {
    const items: number[] = [];
    for (let minute = minMinute; minute <= maxMinute; minute += 60) {
      items.push(minute);
    }
    return items;
  }, [minMinute, maxMinute]);

  const timeRangeMinutes = maxMinute - minMinute;
  const calendarHeight = timeRangeMinutes * PIXELS_PER_MINUTE;

  const bookingPositionsById = useMemo(() => {
    const byStage = new Map<string, BookingPosition[]>();

    scheduledBookings.forEach((booking) => {
      if (!booking.startTime || !booking.endTime) return;

      const stageId = booking.stage?.id ?? NO_STAGE_ID;
      const startMinutesRaw = getMinutesFromDayStart(booking.startTime);
      const endMinutesRaw = getMinutesFromDayStart(booking.endTime);
      const currentResize =
        resizeState?.bookingId === booking.id ? resizeState : null;

      const snappedStart = clamp(
        snapMinutes(startMinutesRaw),
        minMinute,
        maxMinute - MIN_DURATION_MINUTES
      );
      const snappedEnd = snapMinutes(
        currentResize ? currentResize.currentEndMinutes : endMinutesRaw
      );

      const { start, end } = safeTimeRange(
        snappedStart,
        clamp(snappedEnd, snappedStart + MIN_DURATION_MINUTES, maxMinute)
      );

      const position: BookingPosition = {
        booking,
        stageId,
        startMinutes: start,
        endMinutes: end,
        laneIndex: 0,
        laneCount: 1,
        top: (start - minMinute) * PIXELS_PER_MINUTE,
        height: Math.max(
          MIN_CARD_HEIGHT_PX,
          (end - start) * PIXELS_PER_MINUTE
        ),
      };

      const items = byStage.get(stageId) ?? [];
      items.push(position);
      byStage.set(stageId, items);
    });

    const positioned = new Map<string, BookingPosition>();

    byStage.forEach((items) => {
      items.sort((a, b) => a.startMinutes - b.startMinutes);

      const laneEndMinutes: number[] = [];
      items.forEach((item) => {
        let laneIndex = laneEndMinutes.findIndex(
          (laneEndMinute) => laneEndMinute <= item.startMinutes
        );

        if (laneIndex === -1) {
          laneIndex = laneEndMinutes.length;
          laneEndMinutes.push(item.endMinutes);
        } else {
          laneEndMinutes[laneIndex] = item.endMinutes;
        }

        item.laneIndex = laneIndex;
      });

      const laneCount = Math.max(1, laneEndMinutes.length);
      items.forEach((item) => {
        item.laneCount = laneCount;
        positioned.set(item.booking.id, item);
      });
    });

    return positioned;
  }, [getMinutesFromDayStart, maxMinute, minMinute, resizeState, scheduledBookings]);

  const stageBookings = useMemo(() => {
    const grouped = new Map<string, BookingPosition[]>();

    bookingPositionsById.forEach((position) => {
      const items = grouped.get(position.stageId) ?? [];
      items.push(position);
      grouped.set(position.stageId, items);
    });

    grouped.forEach((items, stageId) => {
      grouped.set(
        stageId,
        [...items].sort((a, b) => a.startMinutes - b.startMinutes)
      );
    });

    return grouped;
  }, [bookingPositionsById]);

  const filteredBands = useMemo(() => {
    const term = bandSearch.trim().toLowerCase();
    const items = allBandsData?.bands ?? [];
    const matches = term
      ? items.filter((item) => item.name.toLowerCase().includes(term))
      : items;
    return matches.slice(0, 250);
  }, [allBandsData, bandSearch]);

  const filteredAnnouncementBands = useMemo(() => {
    const term = bandSearch.trim().toLowerCase();
    const scheduledLocalIds = new Set(
      localBands
        .filter((item) => !!item.startTime && !!item.endTime)
        .map((item) => item.id)
    );
    const matches = term
      ? announcementBands.filter((item) =>
          item.bandName.toLowerCase().includes(term)
        )
      : announcementBands;
    return matches
      .filter((item) => !scheduledLocalIds.has(item.bookingId))
      .slice(0, 250);
  }, [announcementBands, bandSearch, localBands]);

  const pendingCreateBookings = useMemo(
    () => localBands.filter((item) => item.isNew),
    [localBands]
  );

  const pendingUpdateBookings = useMemo(
    () =>
      localBands.filter(
        (item) =>
          !item.isNew &&
          dirtyBookingIds.has(item.id) &&
          !deletedBookingIds.has(item.id)
      ),
    [localBands, dirtyBookingIds, deletedBookingIds]
  );

  const pendingDeleteIds = useMemo(
    () => Array.from(deletedBookingIds),
    [deletedBookingIds]
  );

  const hasPendingChanges =
    pendingCreateBookings.length > 0 ||
    pendingUpdateBookings.length > 0 ||
    pendingDeleteIds.length > 0;

  const applyLocalBookingChange = useCallback(
    (bookingId: string, startISO: string, endISO: string, stageId: string) => {
      setLocalBands((current) =>
        current.map((item) => {
          if (item.id !== bookingId) return item;

          return {
            ...item,
            startTime: startISO,
            endTime: endISO,
            stage:
              stageId === NO_STAGE_ID
                ? null
                : {
                    id: stageId,
                    name:
                      stageNameLookup.get(stageId) ??
                      item.stage?.name ??
                      "Unknown stage",
                  },
          };
        })
      );

      if (!bookingId.startsWith(TEMP_BOOKING_PREFIX)) {
        setDirtyBookingIds((current) => {
          if (current.has(bookingId)) return current;
          const next = new Set(current);
          next.add(bookingId);
          return next;
        });
      }
    },
    [stageNameLookup]
  );

  const handleDeleteBooking = useCallback((booking: CalendarBooking) => {
    if (!booking.id) return;

    const shouldDelete = window.confirm(
      `Delete booking for ${booking.band.name}?`
    );
    if (!shouldDelete) return;

    setLocalBands((current) => current.filter((item) => item.id !== booking.id));
    setDraggingBookingId((current) => (current === booking.id ? null : current));
    setBookingEditState((current) => {
      if (!current || current.bookingId !== booking.id) return current;
      return null;
    });

    if (booking.isNew) {
      return;
    }

    setDeletedBookingIds((current) => {
      if (current.has(booking.id)) return current;
      const next = new Set(current);
      next.add(booking.id);
      return next;
    });

    setDirtyBookingIds((current) => {
      if (!current.has(booking.id)) return current;
      const next = new Set(current);
      next.delete(booking.id);
      return next;
    });
  }, []);

  const handleOpenBookingEditor = useCallback(
    (booking: CalendarBooking) => {
      const editDate =
        getScheduleDayKey(booking.startTime, "YYYY-MM-DD") ??
        selectedDayStart.format("YYYY-MM-DD");

      setBookingEditState({
        bookingId: booking.id,
        date: editDate,
        stageId: booking.stage?.id ?? NO_STAGE_ID,
      });
      setIsBookingEditorOpen(true);
    },
    [getScheduleDayKey, selectedDayStart]
  );

  const handleCloseBookingEditor = useCallback(() => {
    setIsBookingEditorOpen(false);
  }, []);

  const handleApplyBookingEditor = useCallback(() => {
    if (!bookingEditState) return;

    const booking = localBands.find((item) => item.id === bookingEditState.bookingId);
    if (!booking) {
      setIsBookingEditorOpen(false);
      setBookingEditState(null);
      return;
    }

    if (!booking.startTime || !booking.endTime) {
      toast.error("Booking must have start and end time before changing day.");
      return;
    }

    const currentStart = dayjs.utc(booking.startTime);
    const currentEnd = dayjs.utc(booking.endTime);
    const targetDayStart = dayjs.utc(`${bookingEditState.date}T00:00:00Z`);

    if (!currentStart.isValid() || !currentEnd.isValid() || !targetDayStart.isValid()) {
      toast.error("Unable to update booking date.");
      return;
    }

    const currentOffsetMinutes = currentStart.diff(selectedDayStart, "minute");
    const durationMinutes = Math.max(
      MIN_DURATION_MINUTES,
      currentEnd.diff(currentStart, "minute")
    );
    const nextStart = targetDayStart.add(currentOffsetMinutes, "minute");
    const nextEnd = nextStart.add(durationMinutes, "minute");

    applyLocalBookingChange(
      booking.id,
      nextStart.toISOString(),
      nextEnd.toISOString(),
      bookingEditState.stageId
    );
    setIsBookingEditorOpen(false);
  }, [applyLocalBookingChange, bookingEditState, localBands, selectedDayStart]);

  const handleDiscardChanges = useCallback(() => {
    if (!hasPendingChanges) return;

    const shouldDiscard = window.confirm("Discard unsaved calendar changes?");
    if (!shouldDiscard) return;

    setDirtyBookingIds(new Set<string>());
    setDeletedBookingIds(new Set<string>());
    setLocalBands(
      bands.map((item) => ({
        ...item,
        isNew: false,
      }))
    );
  }, [bands, hasPendingChanges]);

  const handleSaveChanges = useCallback(async () => {
    if (!hasPendingChanges || isSavingChanges) return;

    setIsSavingChanges(true);

    let savedCount = 0;
    let anySuccess = false;
    let firstError: string | null = null;

    const failedDeleteIds = new Set<string>();
    const successfulUpdateIds = new Set<string>();
    const successfulCreateTempIds: string[] = [];

    try {
      const updateInputs = pendingUpdateBookings
        .filter((item) => !!item.startTime && !!item.endTime)
        .map((item) => {
          const start = dayjs.utc(item.startTime as string);
          const bookingDate = new Date(
            Date.UTC(start.year(), start.month(), start.date())
          ).toISOString();

          return {
            id: item.id,
            date: bookingDate,
            startTime: item.startTime as string,
            endTime: item.endTime as string,
            stageId: item.stage?.id ?? null,
          };
        });

      if (updateInputs.length > 0) {
        try {
          await updateBookings({
            variables: {
              bandBookings: updateInputs,
            },
            context: SUPPRESS_AUTO_EXPORT_TOAST_CONTEXT,
          });

          savedCount += updateInputs.length;
          anySuccess = true;
          pendingUpdateBookings.forEach((item) => {
            successfulUpdateIds.add(item.id);
          });
        } catch (error) {
          firstError = firstError ?? getUpdateErrorMessage(error);
        }
      }

      for (const bookingId of pendingDeleteIds) {
        try {
          await deleteBooking({
            variables: { bookingId },
            context: SUPPRESS_AUTO_EXPORT_TOAST_CONTEXT,
          });
          savedCount += 1;
          anySuccess = true;
        } catch (error) {
          failedDeleteIds.add(bookingId);
          firstError = firstError ?? getUpdateErrorMessage(error);
        }
      }

      if (pendingDeleteIds.length > 0) {
        setDeletedBookingIds(failedDeleteIds);
      }

      for (const booking of pendingCreateBookings) {
        if (!booking.startTime || !booking.endTime) {
          firstError = firstError ?? "New booking is missing start/end time";
          continue;
        }
        if (!booking.announcementId) {
          firstError = firstError ?? "New booking is missing announcement";
          continue;
        }

        const start = dayjs.utc(booking.startTime);
        const bookingDate = new Date(
          Date.UTC(start.year(), start.month(), start.date())
        ).toISOString();

        try {
          await createBooking({
            variables: {
              bandId: booking.band.id,
              announcementId: booking.announcementId,
              editionId: null,
              stageId: booking.stage?.id ?? null,
              startTime: booking.startTime,
              endTime: booking.endTime,
              date: bookingDate,
            },
            context: SUPPRESS_AUTO_EXPORT_TOAST_CONTEXT,
          });
          savedCount += 1;
          anySuccess = true;
          successfulCreateTempIds.push(booking.id);
        } catch (error) {
          firstError = firstError ?? getUpdateErrorMessage(error);
        }
      }

      if (successfulCreateTempIds.length > 0) {
        setLocalBands((current) =>
          current.filter((item) => !successfulCreateTempIds.includes(item.id))
        );
      }

      if (anySuccess && onRefresh) {
        await onRefresh();
      }

      if (successfulUpdateIds.size > 0) {
        setDirtyBookingIds((current) => {
          const next = new Set(current);
          successfulUpdateIds.forEach((bookingId) => next.delete(bookingId));
          return next;
        });
      }

      if (firstError) {
        if (savedCount > 0) {
          toast.error(
            `Saved ${savedCount} changes, but some failed: ${firstError}`
          );
        } else {
          toast.error(`Unable to save changes: ${firstError}`);
        }
      } else {
        toast.success(`Saved ${savedCount} change${savedCount === 1 ? "" : "s"}`);
      }
    } finally {
      setIsSavingChanges(false);
    }
  }, [
    createBooking,
    deleteBooking,
    hasPendingChanges,
    isSavingChanges,
    onRefresh,
    pendingCreateBookings,
    pendingDeleteIds,
    pendingUpdateBookings,
    updateBookings,
  ]);

  const getDropMinutes = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const bounds = event.currentTarget.getBoundingClientRect();
      const yPosition = event.clientY - bounds.top;
      const minuteValue = minMinute + yPosition / PIXELS_PER_MINUTE;
      return clamp(
        snapMinutes(minuteValue),
        minMinute,
        maxMinute - MIN_DURATION_MINUTES
      );
    },
    [maxMinute, minMinute]
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>, stageId: string) => {
      event.preventDefault();
      setActiveDropStageId(null);
      setDraggingBookingId(null);

      const payload = parseDragPayload(
        event.dataTransfer.getData(DRAG_PAYLOAD_TYPE)
      );
      if (!payload) return;

      const droppedMinutes = getDropMinutes(event);

      if (payload.type === "booking") {
        const position = bookingPositionsById.get(payload.bookingId);
        if (!position) return;

        const duration = Math.max(
          MIN_DURATION_MINUTES,
          position.endMinutes - position.startMinutes
        );
        const adjustedStart = clamp(
          snapMinutes(droppedMinutes - payload.offsetMinutes),
          minMinute,
          maxMinute - duration
        );
        const adjustedEnd = adjustedStart + duration;
        const nextStartISO = selectedDayStart.add(adjustedStart, "minute").toISOString();
        const nextEndISO = selectedDayStart.add(adjustedEnd, "minute").toISOString();

        applyLocalBookingChange(position.booking.id, nextStartISO, nextEndISO, stageId);
        return;
      }

      const dragOffsetMinutes =
        typeof payload.offsetMinutes === "number" ? payload.offsetMinutes : 0;
      const newStartMinutes = clamp(
        snapMinutes(droppedMinutes - dragOffsetMinutes),
        minMinute,
        maxMinute - MIN_DURATION_MINUTES
      );
      const newEndMinutes = Math.min(
        maxMinute,
        newStartMinutes + DEFAULT_NEW_BOOKING_DURATION_MINUTES
      );
      const newStart = selectedDayStart.add(newStartMinutes, "minute");
      const newEnd = selectedDayStart.add(newEndMinutes, "minute");
      const stageValue =
        stageId === NO_STAGE_ID
          ? null
          : {
              id: stageId,
              name: stageNameLookup.get(stageId) ?? "Unknown stage",
            };

      if (payload.type === "announcementBooking") {
        const startISO = newStart.toISOString();
        const endISO = newEnd.toISOString();
        const dateISO = new Date(
          Date.UTC(newStart.year(), newStart.month(), newStart.date())
        ).toISOString();
        const duration = `${newEnd.diff(newStart, "minute")}`;

        setLocalBands((current) => {
          const existingIndex = current.findIndex(
            (item) => item.id === payload.bookingId
          );

          if (existingIndex >= 0) {
            const existing = current[existingIndex];
            const next = [...current];
            next[existingIndex] = {
              ...existing,
              date: dateISO,
              startTime: startISO,
              endTime: endISO,
              duration,
              stage: stageValue,
              source: payload.announcementName,
              announcementId: payload.announcementId,
              isNew: false,
            };
            return next;
          }

          const scheduledBooking: CalendarBooking = {
            id: payload.bookingId,
            bookingId: payload.bookingId,
            showcase: false,
            band: {
              id: payload.bandId,
              name: payload.bandName,
              description: null,
              imageUrl: null,
              bookings: [],
            },
            date: dateISO,
            duration,
            stage: stageValue,
            startTime: startISO,
            endTime: endISO,
            source: payload.announcementName,
            announcementId: payload.announcementId,
            isNew: false,
          };

          return [...current, scheduledBooking];
        });

        setDirtyBookingIds((current) => {
          if (current.has(payload.bookingId)) return current;
          const next = new Set(current);
          next.add(payload.bookingId);
          return next;
        });
        return;
      }

      if (!selectedCreateAnnouncementId) {
        toast.error("Select an announcement before creating a booking.");
        return;
      }
      const tempId = `${TEMP_BOOKING_PREFIX}${nextTempIdRef.current++}`;

      const newBooking: CalendarBooking = {
        id: tempId,
        bookingId: tempId,
        showcase: false,
        band: {
          id: payload.bandId,
          name: payload.bandName,
          description: null,
          imageUrl: null,
          bookings: [],
        },
        date: new Date(
          Date.UTC(newStart.year(), newStart.month(), newStart.date())
        ).toISOString(),
        duration: `${newEnd.diff(newStart, "minute")}`,
        stage: stageValue,
        startTime: newStart.toISOString(),
        endTime: newEnd.toISOString(),
        source:
          announcementNameLookup.get(selectedCreateAnnouncementId) ??
          "Announcement",
        announcementId: selectedCreateAnnouncementId,
        isNew: true,
      };

      setLocalBands((current) => [...current, newBooking]);
    },
    [
      applyLocalBookingChange,
      bookingPositionsById,
      getDropMinutes,
      maxMinute,
      minMinute,
      selectedCreateAnnouncementId,
      selectedDayStart,
      announcementNameLookup,
      stageNameLookup,
    ]
  );

  const handleBandDragStart = useCallback(
    (
      event: DragEvent<HTMLDivElement>,
      band: {
        id: string;
        name: string;
      }
    ) => {
      const sourceBounds = event.currentTarget.getBoundingClientRect();
      const pointerOffsetMinutes = clamp(
        (event.clientY - sourceBounds.top) / PIXELS_PER_MINUTE,
        0,
        DEFAULT_NEW_BOOKING_DURATION_MINUTES
      );
      const payload: BandDragPayload = {
        type: "band",
        bandId: band.id,
        bandName: band.name,
        offsetMinutes: pointerOffsetMinutes,
      };
      event.dataTransfer.setData(DRAG_PAYLOAD_TYPE, JSON.stringify(payload));
      event.dataTransfer.effectAllowed = "copy";
    },
    []
  );

  const handleAnnouncementBookingDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>, booking: AnnouncementBandOption) => {
      const sourceBounds = event.currentTarget.getBoundingClientRect();
      const pointerOffsetMinutes = clamp(
        (event.clientY - sourceBounds.top) / PIXELS_PER_MINUTE,
        0,
        DEFAULT_NEW_BOOKING_DURATION_MINUTES
      );
      const payload: AnnouncementBookingDragPayload = {
        type: "announcementBooking",
        bookingId: booking.bookingId,
        announcementId: booking.announcementId,
        announcementName: booking.announcementName,
        bandId: booking.bandId,
        bandName: booking.bandName,
        offsetMinutes: pointerOffsetMinutes,
      };
      event.dataTransfer.setData(DRAG_PAYLOAD_TYPE, JSON.stringify(payload));
      event.dataTransfer.effectAllowed = "copy";
    },
    []
  );

  useEffect(() => {
    if (!resizeState?.bookingId) return;

    const handleMouseMove = (event: MouseEvent) => {
      setResizeState((current) => {
        if (!current) return current;

        const deltaMinutes = snapMinutes(
          (event.clientY - current.initialClientY) / PIXELS_PER_MINUTE
        );
        const nextEnd = clamp(
          current.initialEndMinutes + deltaMinutes,
          current.startMinutes + MIN_DURATION_MINUTES,
          maxMinute
        );

        if (nextEnd === current.currentEndMinutes) return current;
        return { ...current, currentEndMinutes: nextEnd };
      });
    };

    const handleMouseUp = () => {
      const current = resizeStateRef.current;
      if (!current) return;

      suppressBookingClickUntilRef.current = Date.now() + 350;
      setResizeState(null);
      if (current.currentEndMinutes === current.initialEndMinutes) return;

      const position = bookingPositionsById.get(current.bookingId);
      if (!position) return;

      const nextStartISO = selectedDayStart
        .add(current.startMinutes, "minute")
        .toISOString();
      const nextEndISO = selectedDayStart
        .add(current.currentEndMinutes, "minute")
        .toISOString();

      applyLocalBookingChange(
        position.booking.id,
        nextStartISO,
        nextEndISO,
        position.stageId
      );
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [applyLocalBookingChange, bookingPositionsById, maxMinute, resizeState?.bookingId, selectedDayStart]);

  const handleCalendarPanStart = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      if (!shouldStartCalendarPan(event.target)) return;

      const scrollContainer = calendarScrollRef.current;
      if (!scrollContainer) return;

      calendarPanStateRef.current = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        startScrollLeft: scrollContainer.scrollLeft,
        startScrollTop: scrollContainer.scrollTop,
      };
      setIsCalendarPanning(true);
      event.preventDefault();
    },
    []
  );

  useEffect(() => {
    if (!isCalendarPanning) return;

    const handleMouseMove = (event: MouseEvent) => {
      const panState = calendarPanStateRef.current;
      const scrollContainer = calendarScrollRef.current;
      if (!panState || !scrollContainer) return;

      const deltaX = event.clientX - panState.startClientX;
      const deltaY = event.clientY - panState.startClientY;

      scrollContainer.scrollLeft = panState.startScrollLeft - deltaX;
      scrollContainer.scrollTop = panState.startScrollTop - deltaY;
    };

    const stopPanning = () => {
      setIsCalendarPanning(false);
      calendarPanStateRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopPanning);
    window.addEventListener("blur", stopPanning);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopPanning);
      window.removeEventListener("blur", stopPanning);
    };
  }, [isCalendarPanning]);

  useEffect(() => {
    if (!isCalendarPanning) return;

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";

    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isCalendarPanning]);

  if (!selectedDayStart.isValid()) {
    return (
      <Alert severity="warning">
        Could not render calendar view because this day is not valid.
      </Alert>
    );
  }

  if (stageColumns.length === 0) {
    return (
      <Alert severity="info">
        No stages found for this edition. Create at least one stage to use the
        calendar scheduler.
      </Alert>
    );
  }

  const gridTemplateColumns = `88px repeat(${filteredStageColumns.length}, minmax(220px, 1fr))`;

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.75}>
        <Button
          variant="text"
          size="small"
          onClick={() => setShowStageFilters((current) => !current)}
          sx={{ alignSelf: "flex-start", px: 0.5, minWidth: 0 }}
        >
          {showStageFilters ? "Hide stage filters" : "Show stage filters"}
        </Button>
        <Collapse in={showStageFilters} unmountOnExit>
          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary">
              Visible stages
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {stageColumns.map((stage) => (
                <FormControlLabel
                  key={stage.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={visibleStageIds.has(stage.id)}
                      onChange={(_, checked) => {
                        setVisibleStageIds((current) => {
                          const next = new Set(current);
                          if (checked) {
                            next.add(stage.id);
                          } else {
                            next.delete(stage.id);
                          }
                          return next;
                        });
                      }}
                    />
                  }
                  label={stage.name}
                  sx={{
                    mr: 1.25,
                    ".MuiFormControlLabel-label": {
                      fontSize: 12,
                    },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Collapse>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Button
          variant="contained"
          onClick={() => {
            void handleSaveChanges();
          }}
          disabled={!hasPendingChanges || isSavingChanges}
        >
          {isSavingChanges ? "Saving..." : "Save Changes"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleDiscardChanges}
          disabled={!hasPendingChanges || isSavingChanges}
        >
          Discard
        </Button>
        <Typography variant="caption" color="text.secondary">
          {pendingUpdateBookings.length} update
          {pendingUpdateBookings.length === 1 ? "" : "s"}, {pendingCreateBookings.length}{" "}
          new, {pendingDeleteIds.length} delete
          {pendingDeleteIds.length === 1 ? "" : "s"}
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", lg: "flex-start" }}
      >
        <Paper
          ref={calendarScrollRef}
          onMouseDown={handleCalendarPanStart}
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
            cursor: isCalendarPanning ? "grabbing" : "grab",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns,
              minWidth: 88 + filteredStageColumns.length * 220,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ p: 1.25 }} />
            {filteredStageColumns.map((stage) => (
              <Box
                key={stage.id}
                sx={{
                  px: 1.25,
                  py: 1.1,
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  backgroundColor:
                    activeDropStageId === stage.id
                      ? "rgba(25, 118, 210, 0.12)"
                      : "background.paper",
                }}
              >
                <Typography variant="subtitle2">{stage.name}</Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns,
              minWidth: 88 + filteredStageColumns.length * 220,
              height: calendarHeight + 6,
            }}
          >
            <Box
              sx={{
                position: "relative",
                borderRight: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              {hourMarkers.map((minute) => (
                <Box
                  key={`label-${minute}`}
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: (minute - minMinute) * PIXELS_PER_MINUTE - 9,
                    fontSize: 11,
                    color: "text.secondary",
                  }}
                >
                  {formatTimelineLabel(selectedDayStart, minute)}
                </Box>
              ))}
            </Box>

            {filteredStageColumns.map((stage) => {
              const items = stageBookings.get(stage.id) ?? [];
              return (
                <Box
                  key={stage.id}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setActiveDropStageId(stage.id);
                  }}
                  onDragLeave={() => {
                    setActiveDropStageId((current) =>
                      current === stage.id ? null : current
                    );
                  }}
                  onDrop={(event) => {
                    handleDrop(event, stage.id);
                  }}
                  sx={{
                    position: "relative",
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    backgroundColor:
                      activeDropStageId === stage.id
                        ? "rgba(25, 118, 210, 0.08)"
                        : "background.default",
                  }}
                >
                  {hourMarkers.map((minute) => (
                    <Box
                      key={`${stage.id}-${minute}`}
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: (minute - minMinute) * PIXELS_PER_MINUTE,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  ))}

                  {items.map((position) => {
                    const { booking } = position;
                    const hasPendingBookingChange =
                      Boolean(booking.isNew) || dirtyBookingIds.has(booking.id);
                    const cardLeft = `calc(${(position.laneIndex / position.laneCount) * 100}% + 4px)`;
                    const cardWidth = `calc(${100 / position.laneCount}% - 8px)`;
                    const startLabel = formatTimelineLabel(
                      selectedDayStart,
                      position.startMinutes
                    );
                    const endLabel = formatTimelineLabel(
                      selectedDayStart,
                      position.endMinutes
                    );

                    return (
                      <Box
                        key={booking.id}
                        draggable
                        onClick={(event) => {
                          if (
                            Date.now() < suppressBookingClickUntilRef.current ||
                            resizeStateRef.current?.bookingId === booking.id
                          ) {
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                          }
                          handleOpenBookingEditor(booking);
                        }}
                        onDragStart={(event) => {
                          const targetBounds =
                            event.currentTarget.getBoundingClientRect();
                          const pointerOffset = clamp(
                            (event.clientY - targetBounds.top) /
                              PIXELS_PER_MINUTE,
                            0,
                            position.endMinutes - position.startMinutes
                          );

                          const payload: BookingDragPayload = {
                            type: "booking",
                            bookingId: booking.id,
                            offsetMinutes: pointerOffset,
                          };

                          event.dataTransfer.setData(
                            DRAG_PAYLOAD_TYPE,
                            JSON.stringify(payload)
                          );
                          event.dataTransfer.effectAllowed = "move";
                          setDraggingBookingId(booking.id);
                        }}
                        onDragEnd={() => {
                          setDraggingBookingId((current) =>
                            current === booking.id ? null : current
                          );
                          setActiveDropStageId(null);
                        }}
                        sx={{
                          position: "absolute",
                          top: position.top,
                          left: cardLeft,
                          width: cardWidth,
                          height: position.height,
                          borderRadius: 1,
                          border: "1px solid rgba(255,255,255,0.28)",
                          boxShadow: 1,
                          backgroundColor: hasPendingBookingChange
                            ? "rgba(25, 118, 210, 0.95)"
                            : "rgba(46, 125, 50, 0.95)",
                          color: "#fff",
                          px: 0.8,
                          py: 0.6,
                          cursor: "move",
                          overflow: "hidden",
                          opacity: draggingBookingId === booking.id ? 0.45 : 1,
                          userSelect: "none",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1.2,
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            pr: 2,
                          }}
                        >
                          {booking.band.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            lineHeight: 1.2,
                            display: "block",
                            opacity: 0.88,
                          }}
                        >
                          {startLabel} - {endLabel}
                        </Typography>
                        {booking.isNew && (
                          <Typography
                            variant="caption"
                            sx={{
                              lineHeight: 1.2,
                              display: "block",
                              opacity: 0.9,
                              fontWeight: 700,
                            }}
                          >
                            Unsaved
                          </Typography>
                        )}
                        {booking.source && booking.source !== "Edition" && (
                          <Typography
                            variant="caption"
                            sx={{
                              lineHeight: 1.2,
                              display: "block",
                              opacity: 0.86,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {booking.source}
                          </Typography>
                        )}

                        <Tooltip title="Delete booking">
                          <IconButton
                            size="small"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleDeleteBooking(booking);
                            }}
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              color: "#fff",
                              p: 0.35,
                              backgroundColor: "rgba(0, 0, 0, 0.25)",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.38)",
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>

                        <Box
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            suppressBookingClickUntilRef.current = Date.now() + 350;
                            setResizeState({
                              bookingId: booking.id,
                              initialClientY: event.clientY,
                              initialEndMinutes: position.endMinutes,
                              currentEndMinutes: position.endMinutes,
                              startMinutes: position.startMinutes,
                            });
                          }}
                          sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 8,
                            cursor: "ns-resize",
                            backgroundColor: "rgba(255,255,255,0.35)",
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          {filteredStageColumns.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Alert severity="info">
                No stages selected. Tick at least one stage to show calendar
                columns.
              </Alert>
            </Box>
          )}
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            width: { xs: "100%", lg: 320 },
            maxWidth: "100%",
            p: 1.5,
            flexShrink: 0,
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">
              {sidebarTab === "allBands"
                ? "Drag Bands To Create Booking"
                : "Schedule Announcement Bands"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sidebarTab === "allBands"
                ? "Drag a band onto a stage/time slot. Existing bookings can also be dragged between stages and resized from their bottom edge."
                : "Drag an unscheduled announcement booking onto the calendar to assign stage and times."}
            </Typography>
            <Tabs
              value={sidebarTab}
              onChange={(_, nextValue: "announcementBands" | "allBands") =>
                setSidebarTab(nextValue)
              }
              variant="fullWidth"
              sx={{ minHeight: 36 }}
            >
              <Tab
                value="announcementBands"
                label="Announcement Bands"
                sx={{ minHeight: 36 }}
              />
              <Tab value="allBands" label="Create Booking" sx={{ minHeight: 36 }} />
            </Tabs>
            {sidebarTab === "allBands" && (
              <FormControl fullWidth size="small">
                <InputLabel id="create-booking-target-label">
                  Create In Announcement
                </InputLabel>
                <Select
                  labelId="create-booking-target-label"
                  value={selectedCreateAnnouncementId}
                  label="Create In Announcement"
                  disabled={announcements.length === 0}
                  onChange={(event) =>
                    setSelectedCreateAnnouncementId(event.target.value as string)
                  }
                >
                  {announcements.length === 0 ? (
                    <MenuItem value="" disabled>
                      No announcements available
                    </MenuItem>
                  ) : (
                    announcements.map((announcement) => (
                      <MenuItem key={announcement.id} value={announcement.id}>
                        {announcement.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
            <Divider />
            <TextField
              value={bandSearch}
              onChange={(event) => setBandSearch(event.target.value)}
              placeholder="Search bands..."
              size="small"
              fullWidth
            />
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                minHeight: { xs: 190, lg: 420 },
                maxHeight: { xs: 260, lg: 560 },
                overflow: "auto",
              }}
            >
              {sidebarTab === "announcementBands" ? (
                filteredAnnouncementBands.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ px: 1.5, py: 2 }}
                  >
                    {announcementBands.length === 0
                      ? "No announcement bands."
                      : "No matching bands."}
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {filteredAnnouncementBands.map((band) => (
                      <ListItemButton
                        key={band.bookingId}
                        draggable
                        onDragStart={(event) => {
                          handleAnnouncementBookingDragStart(event, band);
                        }}
                        sx={{
                          cursor: "grab",
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{
                            variant: "body2",
                            noWrap: true,
                          }}
                          primary={band.bandName}
                          secondary={band.announcementName}
                          secondaryTypographyProps={{
                            variant: "caption",
                            noWrap: true,
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )
              ) : allBandsLoading ? (
                <Box
                  sx={{
                    py: 5,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={22} />
                </Box>
              ) : filteredBands.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 1.5, py: 2 }}
                >
                  No matching bands.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filteredBands.map((band) => (
                    <ListItemButton
                      key={band.id}
                      draggable
                      onDragStart={(event) => {
                        handleBandDragStart(event, band);
                      }}
                      sx={{
                        cursor: "grab",
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          variant: "body2",
                          noWrap: true,
                        }}
                        primary={band.name}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          Day: {selectedDayStart.format("dddd YYYY/MM/DD")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {filteredStageColumns.length} of {stageColumns.length} stage
          {stageColumns.length === 1 ? "" : "s"} shown
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {scheduledBookings.length} booking
          {scheduledBookings.length === 1 ? "" : "s"}
        </Typography>
        {unscheduledCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {unscheduledCount} booking
            {unscheduledCount === 1 ? "" : "s"} hidden from this day view
          </Typography>
        )}
      </Stack>

      <Dialog
        open={isBookingEditorOpen}
        onClose={handleCloseBookingEditor}
        TransitionProps={{
          onExited: () => {
            setBookingEditState(null);
          },
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Edit Booking</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {bookingBeingEdited?.band.name ?? "Booking"}
            </Typography>
            <TextField
              label="Day"
              type="date"
              value={bookingEditState?.date ?? ""}
              onChange={(event) => {
                const nextDate = event.target.value;
                setBookingEditState((current) =>
                  current
                    ? {
                        ...current,
                        date: nextDate,
                      }
                    : current
                );
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="booking-edit-stage-label">Stage</InputLabel>
              <Select
                labelId="booking-edit-stage-label"
                value={bookingEditState?.stageId ?? NO_STAGE_ID}
                label="Stage"
                onChange={(event) => {
                  const nextStageId = event.target.value as string;
                  setBookingEditState((current) =>
                    current
                      ? {
                          ...current,
                          stageId: nextStageId,
                        }
                      : current
                  );
                }}
              >
                <MenuItem value={NO_STAGE_ID}>Unassigned</MenuItem>
                {bookingStageOptions.map((stage) => (
                  <MenuItem key={stage.id} value={stage.id}>
                    {stage.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBookingEditor}>Cancel</Button>
          <Button onClick={handleApplyBookingEditor} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EditEditionBandsCalendarView;
